import { AxiosError } from 'axios';

import { StatusCodes, StatusMessage } from '../constants/status-codes';

export enum ErrorCode {
    UNIDENTIFIED,
}

export type ErrorStatusCode = ErrorCode | StatusCodes;

const DEFAULT_ERROR = 'An unexpected error occurred. Please try again';

export class BaseError {
    constructor(
        readonly message: string = DEFAULT_ERROR,
        readonly status: ErrorStatusCode = ErrorCode.UNIDENTIFIED,
        readonly detail: Record<any, any> | string | Array<any> | null = null,
    ) {}

    static fromJSON(axiosError: AxiosError<any>): BaseError {
        if (axiosError.code === 'ECONNABORTED') {
            return new BaseError(`Request Timeout (${axiosError.message})`);
        }

        if (!axiosError.response) {
            return new BaseError(
                `Network Error: Unable to reach the server. Please check your internet connection and try again.`,
            );
        }

        const { status, data } = axiosError.response;

        // Default error message for server-side errors
        let message = StatusMessage[status as StatusCodes] ?? DEFAULT_ERROR;

        if (status >= 500) {
            // Properly handle server errors
            message = `Server Error (${status}): Something went wrong on our end. Please try again later.`;
        } else if (status >= 400) {
            // Client errors (4xx)
            const detail = data?.detail;
            if (detail) {
                if (BaseError.isDetailArray(detail)) {
                    message = detail[0]?.msg ?? message;
                } else if (BaseError.isDetailString(detail)) {
                    message = detail;
                } else if (BaseError.isDetailRecord(detail)) {
                    message = detail?.message ?? message;
                }
            }
        }

        return new BaseError(message, status, data?.detail || null);
    }

    private static isDetailRecord(detail: any): boolean {
        return typeof detail === 'object' && !Array.isArray(detail);
    }

    private static isDetailString(detail: any): boolean {
        return typeof detail === 'string';
    }

    private static isDetailArray(detail: any): boolean {
        return Array.isArray(detail);
    }

    getDetailRecord(): Record<any, any> | null {
        return BaseError.isDetailRecord(this.detail)
            ? (this.detail as Record<any, any>)
            : null;
    }

    getDetailString(): string | null {
        return BaseError.isDetailString(this.detail)
            ? (this.detail as string)
            : null;
    }

    getDetailArray(): Array<any> | null {
        return BaseError.isDetailArray(this.detail)
            ? (this.detail as Array<any>)
            : null;
    }
}
